import { useState } from 'react';
import { Box, Button, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/NotificationsNoneOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { fromNow } from '../../lib/format';
import type { NotificationItem } from '../../lib/types';
import { ease, orange, tokens } from '../../app/theme';

export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const count = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.get<{ unread: number }>('/notifications/unread-count'),
    refetchInterval: 30_000,
  });
  const list = useQuery({
    queryKey: ['notifications', 'latest'],
    queryFn: () => api.page<NotificationItem>('/notifications', { limit: 8 }),
    enabled: !!anchor,
  });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const open = (n: NotificationItem) => {
    setAnchor(null);
    if (!n.isRead) markRead.mutate(n.id);
    if (n.link) navigate(n.link);
  };

  const unread = count.data?.unread ?? 0;
  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={`${unread} unread notifications`}
          sx={{
            position: 'relative',
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: `1px solid ${tokens.line}`,
            bgcolor: tokens.surface,
            color: tokens.muted,
            '&:hover': { bgcolor: tokens.hover, color: tokens.ink2 },
          }}
        >
          {unread > 0 && (
            // TailAdmin unread indicator: orange dot with a ping ripple.
            <Box component="span" sx={{ position: 'absolute', top: 2, right: 0, width: 8, height: 8, zIndex: 1, display: 'flex' }}>
              <Box component="span" sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: orange[400], opacity: 0.75, animation: 'ta-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite' }} />
              <Box component="span" sx={{ position: 'relative', width: 8, height: 8, borderRadius: '50%', bgcolor: orange[400] }} />
            </Box>
          )}
          <NotificationsIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { mt: 2, width: 361, maxWidth: '95vw', maxHeight: 480, p: 1.5, display: 'flex', flexDirection: 'column', bgcolor: tokens.popover },
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pb: 1.5, mb: 1.5, borderBottom: `1px solid ${tokens.line}` }}>
          <Typography fontSize={18} fontWeight={600} color="text.primary">
            Notifications
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Button size="small" disabled={!unread} onClick={() => markAll.mutate()}>
              Mark all read
            </Button>
            <IconButton size="small" onClick={() => setAnchor(null)} aria-label="Close notifications" sx={{ color: tokens.muted }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, overflowY: 'auto', flex: 1 }}>
          {list.data?.data.length === 0 && (
            <Typography fontSize={14} color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              You're all caught up
            </Typography>
          )}
          {list.data?.data.map((n) => (
            <li key={n.id}>
              <Box
                component="button"
                type="button"
                role="menuitem"
                onClick={() => open(n)}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  width: '100%',
                  textAlign: 'left',
                  font: 'inherit',
                  border: 0,
                  borderBottom: `1px solid ${tokens.lineSoft}`,
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  px: 1.25,
                  py: 1.5,
                  borderRadius: 2,
                  transition: `background-color .2s ${ease}`,
                  '&:hover': { bgcolor: tokens.hover },
                }}
              >
                <Box sx={{ position: 'relative', flexShrink: 0, alignSelf: 'flex-start' }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: tokens.brandSoft, color: tokens.brandText }}>
                    <NotificationsIcon sx={{ fontSize: 20 }} />
                  </Box>
                  {!n.isRead && (
                    <Box sx={{ position: 'absolute', right: 0, bottom: 0, width: 10, height: 10, borderRadius: '50%', bgcolor: '#12b76a', border: `1.5px solid ${tokens.popover}` }} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize={14} color={n.isRead ? 'text.secondary' : 'text.primary'} fontWeight={n.isRead ? 400 : 500} sx={{ mb: 0.5 }}>
                    {n.title}
                  </Typography>
                  <Typography fontSize={12} color="text.secondary">
                    {fromNow(n.createdAt)}
                  </Typography>
                </Box>
              </Box>
            </li>
          ))}
        </Box>
        <Button
          variant="outlined"
          fullWidth
          sx={{ mt: 1.5 }}
          onClick={() => {
            setAnchor(null);
            navigate('/notifications');
          }}
        >
          View all notifications
        </Button>
      </Popover>
    </>
  );
}
